/**
 * Barcode / QR scanners in keyboard (HID) mode type a whole code in a few milliseconds and press
 * Enter (T11-07). A burst of printable keys less than `maxGapMs` apart ending in Enter is a scan —
 * even when no search box has focus — and is treated as a lookup. People type much slower
 * (≥ 60 ms between keys), so they never trigger it.
 *
 * Framework-free: `feed()` receives key presses, returns the scanned code on Enter.
 */

export interface ScanKey {
  key: string;
  /** `event.timeStamp` or `performance.now()` (ms). */
  time: number;
}

export interface ScannerOptions {
  /** Longest pause between two characters of one scan (default 30 ms). */
  maxGapMs?: number;
  /** Shortest code (default 4 — cert numbers are 8+, item codes 10+). */
  minLength?: number;
}

export class ScannerDetector {
  private buffer = "";
  private last = Number.NEGATIVE_INFINITY;
  private readonly maxGapMs: number;
  private readonly minLength: number;

  constructor(options: ScannerOptions = {}) {
    this.maxGapMs = options.maxGapMs ?? 30;
    this.minLength = options.minLength ?? 4;
  }

  /** Returns the code when this key finished a scan, otherwise `null`. */
  feed({ key, time }: ScanKey): string | null {
    const gap = time - this.last;
    if (key === "Enter") {
      const code = this.buffer;
      const fast = gap <= this.maxGapMs;
      this.reset();
      return fast && code.length >= this.minLength ? code : null;
    }
    if (key.length !== 1) {
      // Shift (scanners send it for capitals), arrows… — ignored, they do not break a burst
      return null;
    }
    if (this.buffer && gap > this.maxGapMs) {
      this.buffer = "";
    }
    this.buffer += key;
    this.last = time;
    return null;
  }

  /** A burst is in progress (the page should not act on these keys as shortcuts). */
  get pending(): boolean {
    return this.buffer.length > 1;
  }

  reset(): void {
    this.buffer = "";
    this.last = Number.NEGATIVE_INFINITY;
  }
}
